import { createStore } from '@xstate/store';
import type { ADR, AdrRelation, AdrRelationType, AdrCommentNode } from '../types';

// ============================================
// ADR Store - Verwaltet Architecture Decision Records
// ============================================

const ADR_LOCAL_STORAGE_KEY = 'roadmap_2026_adr';
const PERSISTENCE_DEBOUNCE_MS = 500;

export interface AdrContext {
  adrs: ADR[];
}

// Migration: convert old string fields to BlockNote block arrays
function migrateField(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    return [{ type: 'paragraph', content: [{ type: 'text', text: value }] }];
  }
  return [];
}

function migrateAdr(adr: any): ADR {
  return {
    ...adr,
    context: migrateField(adr.context),
    decision: migrateField(adr.decision),
    consequences: migrateField(adr.consequences),
  };
}

// Lade ADRs aus LocalStorage
function loadAdrsFromLocalStorage(): ADR[] {
  try {
    const saved = localStorage.getItem(ADR_LOCAL_STORAGE_KEY);
    if (!saved) return [];

    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map(migrateAdr);
  } catch {
    return [];
  }
}

// Speichere ADRs in LocalStorage
function saveAdrsToLocalStorage(adrs: ADR[]): void {
  localStorage.setItem(ADR_LOCAL_STORAGE_KEY, JSON.stringify(adrs));
}

const initialContext: AdrContext = {
  adrs: loadAdrsFromLocalStorage(),
};

// ============================================
// Inverse Relation Mapping
// ============================================

const INVERSE_RELATION: Record<AdrRelationType, AdrRelationType> = {
  supersedes: 'superseded_by',
  superseded_by: 'supersedes',
  amends: 'amended_by',
  amended_by: 'amends',
  relates_to: 'relates_to',
};

// ============================================
// Comment Tree Helpers
// ============================================

function addCommentToTree(
  comments: AdrCommentNode[],
  parentCommentId: string | null,
  newComment: AdrCommentNode,
): AdrCommentNode[] {
  if (!parentCommentId) {
    return [...comments, newComment];
  }
  let changed = false;
  const result = comments.map(c => {
    if (c.id === parentCommentId) {
      changed = true;
      return { ...c, children: [...c.children, newComment] };
    }
    const newChildren = addCommentToTree(c.children, parentCommentId, newComment);
    if (newChildren !== c.children) {
      changed = true;
      return { ...c, children: newChildren };
    }
    return c;
  });
  return changed ? result : comments;
}

function setResolvedInTree(
  comments: AdrCommentNode[],
  commentId: string,
  resolved: boolean,
): AdrCommentNode[] {
  let changed = false;
  const result = comments.map(c => {
    if (c.id === commentId) {
      changed = true;
      return setAllResolved(c, resolved);
    }
    const newChildren = setResolvedInTree(c.children, commentId, resolved);
    if (newChildren !== c.children) {
      changed = true;
      return { ...c, children: newChildren };
    }
    return c;
  });
  return changed ? result : comments;
}

function setAllResolved(node: AdrCommentNode, resolved: boolean): AdrCommentNode {
  return {
    ...node,
    resolved,
    children: node.children.map(c => setAllResolved(c, resolved)),
  };
}

// ============================================
// Store
// ============================================

export const adrStore = createStore({
  context: initialContext,

  on: {
    'adr.add': (ctx, event: { adr: ADR }) => {
      if (ctx.adrs.some(a => a.id === event.adr.id)) {
        console.warn(`[adrStore] ADR with ID "${event.adr.id}" already exists`);
        return ctx;
      }
      return {
        ...ctx,
        adrs: [...ctx.adrs, event.adr],
      };
    },

    'adr.update': (ctx, event: { adrId: string; updates: Partial<Omit<ADR, 'id' | 'createdAt'>> }) => {
      const adrIndex = ctx.adrs.findIndex(a => a.id === event.adrId);
      if (adrIndex === -1) {
        console.warn(`[adrStore] ADR with ID "${event.adrId}" not found`);
        return ctx;
      }

      const updatedAdrs = [...ctx.adrs];
      updatedAdrs[adrIndex] = {
        ...updatedAdrs[adrIndex],
        ...event.updates,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...ctx,
        adrs: updatedAdrs,
      };
    },

    'adr.delete': (ctx, event: { adrId: string }) => {
      const adrIndex = ctx.adrs.findIndex(a => a.id === event.adrId);
      if (adrIndex === -1) {
        console.warn(`[adrStore] ADR with ID "${event.adrId}" not found`);
        return ctx;
      }
      // Also remove any relations pointing to this ADR from other ADRs
      const filteredAdrs = ctx.adrs
        .filter(a => a.id !== event.adrId)
        .map(a => ({
          ...a,
          relations: a.relations.filter(r => r.targetAdrId !== event.adrId),
        }));
      return {
        ...ctx,
        adrs: filteredAdrs,
      };
    },

    'adr.addRelation': (ctx, event: { adrId: string; relation: AdrRelation }) => {
      const sourceIndex = ctx.adrs.findIndex(a => a.id === event.adrId);
      const targetIndex = ctx.adrs.findIndex(a => a.id === event.relation.targetAdrId);
      if (sourceIndex === -1 || targetIndex === -1) {
        console.warn(`[adrStore] Source or target ADR not found`);
        return ctx;
      }

      // Check if relation already exists
      const source = ctx.adrs[sourceIndex];
      if (source.relations.some(r => r.targetAdrId === event.relation.targetAdrId && r.type === event.relation.type)) {
        return ctx;
      }

      const updatedAdrs = [...ctx.adrs];

      // Add relation to source
      updatedAdrs[sourceIndex] = {
        ...updatedAdrs[sourceIndex],
        relations: [...updatedAdrs[sourceIndex].relations, event.relation],
        updatedAt: new Date().toISOString(),
      };

      // Add inverse relation to target
      const inverseType = INVERSE_RELATION[event.relation.type];
      const target = updatedAdrs[targetIndex];
      if (!target.relations.some(r => r.targetAdrId === event.adrId && r.type === inverseType)) {
        updatedAdrs[targetIndex] = {
          ...updatedAdrs[targetIndex],
          relations: [...updatedAdrs[targetIndex].relations, { targetAdrId: event.adrId, type: inverseType }],
          updatedAt: new Date().toISOString(),
        };
      }

      return {
        ...ctx,
        adrs: updatedAdrs,
      };
    },

    'adr.removeRelation': (ctx, event: { adrId: string; targetAdrId: string; relationType: AdrRelationType }) => {
      const sourceIndex = ctx.adrs.findIndex(a => a.id === event.adrId);
      const targetIndex = ctx.adrs.findIndex(a => a.id === event.targetAdrId);
      if (sourceIndex === -1) return ctx;

      const updatedAdrs = [...ctx.adrs];

      // Remove relation from source
      updatedAdrs[sourceIndex] = {
        ...updatedAdrs[sourceIndex],
        relations: updatedAdrs[sourceIndex].relations.filter(
          r => !(r.targetAdrId === event.targetAdrId && r.type === event.relationType)
        ),
        updatedAt: new Date().toISOString(),
      };

      // Remove inverse relation from target
      if (targetIndex !== -1) {
        const inverseType = INVERSE_RELATION[event.relationType];
        updatedAdrs[targetIndex] = {
          ...updatedAdrs[targetIndex],
          relations: updatedAdrs[targetIndex].relations.filter(
            r => !(r.targetAdrId === event.adrId && r.type === inverseType)
          ),
          updatedAt: new Date().toISOString(),
        };
      }

      return {
        ...ctx,
        adrs: updatedAdrs,
      };
    },

    'adr.addComment': (ctx, event: { adrId: string; comment: AdrCommentNode; parentCommentId: string | null }) => {
      const adrIndex = ctx.adrs.findIndex(a => a.id === event.adrId);
      if (adrIndex === -1) return ctx;

      const updatedAdrs = [...ctx.adrs];
      updatedAdrs[adrIndex] = {
        ...updatedAdrs[adrIndex],
        comments: addCommentToTree(updatedAdrs[adrIndex].comments, event.parentCommentId, event.comment),
        updatedAt: new Date().toISOString(),
      };

      return {
        ...ctx,
        adrs: updatedAdrs,
      };
    },

    'adr.resolveComment': (ctx, event: { adrId: string; commentId: string }) => {
      const adrIndex = ctx.adrs.findIndex(a => a.id === event.adrId);
      if (adrIndex === -1) return ctx;

      const updatedAdrs = [...ctx.adrs];
      updatedAdrs[adrIndex] = {
        ...updatedAdrs[adrIndex],
        comments: setResolvedInTree(updatedAdrs[adrIndex].comments, event.commentId, true),
        updatedAt: new Date().toISOString(),
      };

      return {
        ...ctx,
        adrs: updatedAdrs,
      };
    },

    'adr.unresolveComment': (ctx, event: { adrId: string; commentId: string }) => {
      const adrIndex = ctx.adrs.findIndex(a => a.id === event.adrId);
      if (adrIndex === -1) return ctx;

      const updatedAdrs = [...ctx.adrs];
      updatedAdrs[adrIndex] = {
        ...updatedAdrs[adrIndex],
        comments: setResolvedInTree(updatedAdrs[adrIndex].comments, event.commentId, false),
        updatedAt: new Date().toISOString(),
      };

      return {
        ...ctx,
        adrs: updatedAdrs,
      };
    },

    'adrs.reset': () => ({
      adrs: [],
    }),

    'adrs.import': (_ctx, event: { adrs: ADR[] }) => ({
      adrs: event.adrs,
    }),
  },
});

// ============================================
// Side Effects: Debounced Persistence
// ============================================

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let lastAdrsHash = '';

function computeAdrsHash(adrs: ADR[]): string {
  return adrs.map(a => `${a.id}:${a.title}:${a.status}:${a.implementationStatus}:${a.scope.type}:${a.relations.length}:${a.people.length}:${a.comments.length}:${a.updatedAt}`).join('|');
}

// Subscribe to changes and persist
adrStore.subscribe((snapshot) => {
  const currentHash = computeAdrsHash(snapshot.context.adrs);

  if (currentHash === lastAdrsHash) return;
  lastAdrsHash = currentHash;

  // Debounced persistence
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveAdrsToLocalStorage(snapshot.context.adrs);
  }, PERSISTENCE_DEBOUNCE_MS);
});

// ============================================
// Helper-Funktionen
// ============================================

export function getAdrsArray(): ADR[] {
  return adrStore.getSnapshot().context.adrs;
}

export function getAdrById(adrId: string): ADR | undefined {
  return adrStore.getSnapshot().context.adrs.find(a => a.id === adrId);
}

export function getAdrsByScope(scope: ADR['scope']): ADR[] {
  const adrs = getAdrsArray();
  return adrs.filter(a => {
    if (scope.type === 'global') return a.scope.type === 'global';
    if (scope.type === 'board') return a.scope.type === 'board' && a.scope.boardId === scope.boardId;
    if (scope.type === 'canvas') return a.scope.type === 'canvas' && a.scope.boardId === scope.boardId && a.scope.treeId === scope.treeId;
    return false;
  });
}

export function getAdrsByBoardId(boardId: string): ADR[] {
  const adrs = getAdrsArray();
  return adrs.filter(a =>
    a.scope.type === 'global' ||
    (a.scope.type === 'board' && a.scope.boardId === boardId) ||
    (a.scope.type === 'canvas' && a.scope.boardId === boardId)
  );
}

export function getAdrsByTreeId(boardId: string, treeId: string): ADR[] {
  const adrs = getAdrsArray();
  return adrs.filter(a =>
    a.scope.type === 'global' ||
    (a.scope.type === 'board' && a.scope.boardId === boardId) ||
    (a.scope.type === 'canvas' && a.scope.boardId === boardId && a.scope.treeId === treeId)
  );
}

export function generateAdrId(title: string): string {
  const baseId = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const existingAdrs = getAdrsArray();
  let id = `adr-${baseId || 'new'}`;
  let counter = 1;

  while (existingAdrs.some(a => a.id === id)) {
    id = `adr-${baseId || 'new'}_${counter}`;
    counter++;
  }

  return id;
}

export type AdrStore = typeof adrStore;
